/**
 * NotInPlan_Screen.tsx — "<Product> isn't in your plan".
 *
 * Reached at /not-in-plan?product=<slug>: from the post-login handoff when the
 * org did not buy the product it came from (navigation/productHandoff), and from
 * a product website that refuses the org itself (navigation/planGate explains
 * the rule). The product's API keeps serving the org's own console — only its
 * website is closed — so the way out is either that console or a plan with the
 * product in it.
 */

import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, Platform } from 'react-native';
import { useRoute } from '@react-navigation/native';

import PrimaryButton from '../../components/primaryButton';
import AuthBrandHeader from '../../components/authBrandHeader';
import { CONTENT } from '../../utils/responsive';
import { useTheme } from '../../context/themeContext';
import { getCurrentUser } from '../../services/auth/auth';
import { resolveReturnTarget } from '../../config/products';
import { orgIdFrom, pricingUrlFor, productDisplayName, subscribedProductsFrom } from '../../navigation/planGate';

function go(url: string) {
  if (Platform.OS === 'web' && typeof window !== 'undefined') window.location.assign(url);
}

const NotInPlan_Screen: React.FC = () => {
  const route = useRoute();
  const { theme } = useTheme();
  const { colors } = theme;
  const product: string | undefined = (route.params as any)?.product || undefined;
  const name = productDisplayName(product);
  const pricingUrl = pricingUrlFor(product);
  // The org's own console: the first product website it did buy. Null until
  // /me answers (or when it bought none we know of) → the platform home.
  const [consoleUrl, setConsoleUrl] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getCurrentUser()
      .then(async (me) => {
        // One line per view, so a wrongly blocked org can be found in the logs.
        // eslint-disable-next-line no-console
        console.info(`[not-in-plan] view: ${JSON.stringify({ product: product ?? null, orgId: orgIdFrom(me) })}`);
        const home = subscribedProductsFrom(me)?.[0];
        const url = home ? await resolveReturnTarget(home, null) : null;
        if (active && url) setConsoleUrl(url);
      })
      .catch(() => { /* signed out or offline — the platform home still works */ });
    return () => { active = false; };
  }, [product]);

  return (
    <View style={[styles.root, { backgroundColor: colors.paper }]}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.line }]}>
          <AuthBrandHeader />
          <Text style={[styles.title, { color: colors.ink }]}>{`${name} isn't in your plan`}</Text>
          <Text style={[styles.sub, { color: colors.ink2 }]}>
            {`Your organisation's plan doesn't include ${name}, so its website can't be opened. Everything your plan includes keeps working as usual.`}
          </Text>

          {pricingUrl ? (
            <PrimaryButton title={`See ${name} plans`} onPress={() => go(pricingUrl)} hoverLift style={styles.button} />
          ) : null}
          <PrimaryButton
            title="Back to your console"
            onPress={() => go(consoleUrl ?? '/')}
            variant={pricingUrl ? 'outline' : 'primary'}
            style={styles.button}
          />
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  root: { flex: 1, ...(Platform.OS === 'web' ? ({ minHeight: '100vh' } as any) : {}) },
  scrollContent: { flexGrow: 1, justifyContent: 'center', alignItems: 'center', paddingVertical: 40, paddingHorizontal: 16 },
  card: {
    width: '100%', maxWidth: CONTENT.form, borderRadius: 24, padding: 32, borderWidth: 1, overflow: 'hidden',
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 24px 60px -18px rgba(15,23,42,0.22)' } as any) : { shadowColor: '#0F172A', shadowOffset: { width: 0, height: 14 }, shadowOpacity: 0.14, shadowRadius: 32, elevation: 10 }),
  },
  title: { fontSize: 26, fontWeight: '900', marginBottom: 8, textAlign: 'center', letterSpacing: -0.8 },
  sub: { fontSize: 14, fontWeight: '500', marginBottom: 22, textAlign: 'center', lineHeight: 22 },
  button: { marginTop: 12, height: 52, minHeight: 52, borderRadius: 14 },
});

export default NotInPlan_Screen;
